/** Which cloud a listing was pulled from. Absent in payloads written before
 *  multi-cloud support existed — those are all AWS. */
export type Cloud = 'aws' | 'azure';

export interface ModelRecord {
  modelId: string;
  modelName: string;
  providerName: string;
  inputModalities: string[];
  outputModalities: string[];
  inferenceTypesSupported: string[];
  modelLifecycleStatus: string | null;
  /** absent in payloads written before the field was collected */
  responseStreamingSupported?: boolean | null;
  isCri: boolean;
  region: string;
  cloud?: Cloud;
}

export interface RegionEntry {
  region: string;
  isCri: boolean;
  /** This specific region's own lifecycle status — a model can be reported
   *  Active in one region and Legacy/deprecated in another, so this must
   *  never be collapsed into one value for the whole model. */
  modelLifecycleStatus: string | null;
  /** Deployment/inference tiers this region offers for this model — e.g.
   *  AWS ON_DEMAND/PROVISIONED, Azure Standard/ProvisionedManaged. Per-region,
   *  since the same model can be Standard in one region and reserved-capacity
   *  only in another. */
  inferenceTypesSupported: string[];
}

export interface InferenceProfile {
  profileId: string;
  profileName: string;
  baseModelId: string;
  prefix: string;
  /** union of destination regions across all sources */
  memberRegions: string[];
  /** per-source routing: source region → destination regions reachable from
   *  it. Destinations (and callability) depend on the source region. Absent
   *  in payloads written before this field was collected. */
  sourceRoutes?: Record<string, string[]>;
}

export interface ZoneDef {
  label: string;
  regions: string[];
  /** prefix of the broader geographic zone that also keeps data within a tolerable boundary */
  parent?: string;
  /** true when AWS publishes no system-defined CRI profiles for this zone */
  noCri?: boolean;
}

// Region IDs are never expected to collide across clouds — AWS IDs always
// contain hyphens (us-east-1), Azure IDs never do (eastus, uksouth) — so a
// single flat regions[] list per zone is safe without a cloud-qualified key.
export const ZONES: Record<string, ZoneDef> = {
  us:   { label: 'United States', regions: [
    'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
    'centralus', 'eastus', 'eastus2', 'northcentralus', 'southcentralus', 'westcentralus', 'westus', 'westus2', 'westus3',
  ] },
  ca:   { label: 'Canada',        regions: ['ca-central-1', 'ca-west-1', 'canadacentral', 'canadaeast'], noCri: true },
  mx:   { label: 'Mexico',        regions: ['mx-central-1', 'mexicocentral'], noCri: true },
  sa:   { label: 'South America', regions: ['sa-east-1', 'brazilsouth', 'brazilsoutheast', 'chilecentral'], noCri: true },
  eu:   { label: 'Europe',        regions: [
    'eu-west-1', 'eu-west-2', 'eu-west-3', 'eu-central-1', 'eu-central-2', 'eu-north-1', 'eu-south-1', 'eu-south-2',
    'austriaeast', 'belgiumcentral', 'denmarkeast', 'francecentral', 'francesouth', 'germanynorth', 'germanywestcentral',
    'italynorth', 'northeurope', 'norwayeast', 'norwaywest', 'polandcentral', 'spaincentral', 'swedencentral',
    'switzerlandnorth', 'switzerlandwest', 'uksouth', 'ukwest', 'westeurope',
  ] },
  me:   { label: 'Middle East',   regions: ['me-central-1', 'me-south-1', 'qatarcentral', 'uaecentral', 'uaenorth'] },
  il:   { label: 'Israel',        regions: ['il-central-1', 'israelcentral'], noCri: true },
  af:   { label: 'Africa',        regions: ['af-south-1', 'southafricanorth', 'southafricawest'], noCri: true },
  // geographyOf() skips any zone with a `parent` (see below), so sub-zones
  // like au/nz only work because their regions are ALSO listed directly
  // here in apac — that's not incidental duplication, it's load-bearing.
  apac: { label: 'Asia Pacific',  regions: [
    'ap-northeast-1', 'ap-northeast-2', 'ap-northeast-3', 'ap-south-1', 'ap-south-2', 'ap-southeast-1', 'ap-southeast-2', 'ap-southeast-3', 'ap-southeast-4', 'ap-southeast-5', 'ap-east-1',
    'centralindia', 'eastasia', 'indiasouthcentral', 'indonesiacentral', 'japaneast', 'japanwest',
    'koreacentral', 'koreasouth', 'malaysiawest', 'southeastasia', 'southindia', 'westindia',
    'australiacentral', 'australiacentral2', 'australiaeast', 'australiasoutheast', 'newzealandnorth',
  ] },
  au:   { label: 'Australia',     regions: ['ap-southeast-2', 'ap-southeast-4', 'australiacentral', 'australiacentral2', 'australiaeast', 'australiasoutheast'], parent: 'apac' },
  nz:   { label: 'New Zealand',   regions: ['newzealandnorth'], parent: 'apac', noCri: true },
};

/** Maps a region's prefix (first hyphenated segment) to the zone it belongs to,
 *  so AWS regions launched after this list was written still land in a zone.
 *  Azure IDs have no hyphen and no equally cheap prefix trick (the geography
 *  word can lead — "uksouth" — or trail — "eastus"), so new Azure regions
 *  need an explicit entry above until then; they land in "Other" until so. */
const ZONE_BY_REGION_PREFIX: Record<string, string> = {
  us: 'us', ca: 'ca', mx: 'mx', sa: 'sa', eu: 'eu',
  me: 'me', il: 'il', af: 'af', ap: 'apac',
};

export function regionInZone(region: string, zoneKey: string): boolean {
  const def = ZONES[zoneKey];
  if (!def) return false;
  if (def.regions.includes(region)) return true;
  // Sub-zones (e.g. au) enumerate their regions explicitly and get no prefix fallback
  if (def.parent) return false;
  return ZONE_BY_REGION_PREFIX[region.split('-')[0]] === zoneKey;
}

export interface ChangelogModelRef {
  modelId: string;
  modelName: string;
  providerName?: string;
  regions?: string[];
  /** Deployment tiers offered per listed region — e.g. AWS ON_DEMAND/
   *  PROVISIONED, Azure Standard/ProvisionedManaged. Absent in entries
   *  computed before this field was collected. */
  regionTiers?: Record<string, string[]>;
}

export interface ChangelogStatusChangeRef {
  modelId: string;
  modelName: string;
  /** regions where this model went from Active (or unknown) to Legacy/deprecated */
  toLegacy?: string[];
  /** regions where this model came back from Legacy/deprecated to Active */
  toActive?: string[];
}

export interface ChangelogEntry {
  date: string;
  cloud: Cloud;
  addedModels: ChangelogModelRef[];
  removedModels: ChangelogModelRef[];
  regionAdditions: ChangelogModelRef[];
  /** absent in entries computed before per-region status diffing existed */
  statusChanges?: ChangelogStatusChangeRef[];
}

export interface AnalyticsDay {
  date: string;
  /** unique client IPs seen that UTC day — an approximation, not a precise
   *  headcount (no cookies/sessions: shared IPs undercount, IP churn overcounts) */
  visitors: number;
}

export interface AnalyticsPayload {
  updatedAt: string;
  /** newest-last, capped server-side to a rolling retention window */
  days: AnalyticsDay[];
}

export interface ApiResponse {
  updatedAt: string;
  regionCount: number;
  modelCount: number;
  /** city/location string keyed by region id, e.g. "N. Virginia" for us-east-1 */
  regionLabels: Record<string, string>;
  models: ModelRecord[];
  inferenceProfiles: InferenceProfile[];
}

/** Aggregated view of a model across all regions it appears in, scoped to one
 *  cloud — the same model offered by two clouds is two separate rows. */
export interface ModelRow {
  modelId: string;
  modelName: string;
  providerName: string;
  cloud: Cloud;
  inputModalities: string[];
  outputModalities: string[];
  inferenceTypesSupported: string[];
  /** true if at least one region serves this model via CRI only (no ON_DEMAND) */
  isCri: boolean;
  /** Lifecycle status lives per-region on RegionEntry, not here — use
   *  lifecycleStatus() from lib/models to summarize across regions. */
  regions: RegionEntry[];
  /** null when the upstream payload predates the responseStreamingSupported field */
  streamingSupported: boolean | null;
  inferenceProfiles: InferenceProfile[];
}

export type SortKey = keyof Pick<
  ModelRow,
  'providerName' | 'modelName' | 'modelId' | 'isCri' | 'regions'
>;
export type SortDir = 'asc' | 'desc';

const _GEO: Record<string, string> = {
  ap: 'Asia Pacific', af: 'Africa', ca: 'Canada', eu: 'Europe',
  il: 'Israel', me: 'Middle East', mx: 'Mexico', sa: 'South America', us: 'US',
};
const _DIR: Record<string, string> = {
  central: 'Central', east: 'East', north: 'North', northeast: 'Northeast',
  northwest: 'Northwest', south: 'South', southeast: 'Southeast',
  southwest: 'Southwest', west: 'West',
};

/** Only a AWS's-shaped (geo-direction-N, hyphenated) ID can be guessed at
 *  this way. This is a last-resort fallback for either cloud — both AWS's
 *  SSM-sourced and Azure's ARM-sourced payloads carry real display names in
 *  regionLabels, so this rarely runs in practice, but a bare Azure ID like
 *  "eastus" has no hyphen to split on and must not fall through to
 *  "undefined". */
function inferRegionCity(r: string): string {
  const parts = r.split('-');
  if (parts.length < 2) return r.toUpperCase();
  const geo = _GEO[parts[0]] ?? parts[0].toUpperCase();
  const dir = _DIR[parts[1]] ?? parts[1];
  return `${geo} ${dir}`;
}

export function regionLabel(r: string, labels: Record<string, string>): string {
  const city = labels[r];
  return city ? `${r} (${city})` : `${r} (${inferRegionCity(r)})`;
}

export function regionCity(r: string, labels: Record<string, string>): string {
  return labels[r] ?? inferRegionCity(r);
}